import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { VideoFormat } from "@/lib/hooks/useImageUpload";
import type { IntroStyle } from "./video-form-utils";
import { RichTextEditor } from "./RichTextEditor";
import { Play, RotateCcw, CircleDot } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useAuth } from "@clerk/clerk-react";
import { config } from "@/config/env";
import placeholderImage from "@/assets/images/preview_landscape.webp";
import portraitPlaceholderImage from "@/assets/images/preview_portrait.webp";

interface WatermarkPreviewProps {
  aspectRatio: VideoFormat;
  watermarkPreview: string;
  watermarkAlignment: string;
  watermarkPadding: number;
  watermarkOpacity: number;
  watermarkSize: number;
  getContainerAlignment: (alignment: string) => React.CSSProperties;
  activeTab?: "intro" | "logo" | "outro";
  logoEnabled?: boolean;
  introEnabled?: boolean;
  introTitle?: string;
  introStyle?: IntroStyle;
  introAnimation?: string;
  introBgColor?: string;
  introBgOpacity?: number;
  introTextColor: string;
  introFont?: string;
  introAlignment?: string;
  contactCard: string;
  playTrigger?: number;
  stopTrigger?: number;
  onPlayStateChange?: (playing: boolean) => void;
  onIntroTitleChange?: (html: string) => void;
  onContactCardChange?: (html: string) => void;
  /** Expose captureIntroClip() to parent form for use at submit time */
  captureIntroClipRef?: React.RefObject<{ captureIntroClip: () => Promise<string[]> } | null> | ((el: { captureIntroClip: () => Promise<string[]> } | null) => void);
  /** Called with the current capture phase so the parent can show progress UI */
  onCapturePhaseChange?: (phase: "processing" | null, progress: number) => void;
}

const getContrastColor = (hexColor: string) => {
  if (!hexColor || hexColor.length < 7) return "#FFFFFF";
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000000" : "#FFFFFF";
};

export const WatermarkPreview = ({
  aspectRatio,
  watermarkPreview,
  watermarkAlignment,
  watermarkPadding,
  watermarkOpacity,
  watermarkSize,
  getContainerAlignment,
  activeTab = "logo",
  logoEnabled = true,
  introEnabled = true,
  introTitle,
  introStyle = "cinematic",
  introAnimation = "fade",
  introBgColor = "#000000",
  introBgOpacity = 75,
  introTextColor,
  introFont,
  introAlignment = "center",
  contactCard,
  playTrigger,
  stopTrigger,
  onPlayStateChange,
  onIntroTitleChange,
  onContactCardChange,
  captureIntroClipRef,
  onCapturePhaseChange,
}: WatermarkPreviewProps) => {
  const { t } = useTranslation();
  const introRef = useRef<HTMLDivElement>(null);
  const captureIntroRef = useRef<HTMLDivElement>(null);
  const captureContainerRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const { contextSafe } = useGSAP({ scope: introRef });

  const playPreview = contextSafe((recording = false) => {
    // NOTE: Preview animation commented out. Will be reimplemented later.
    /*
    if (!introRef.current) return;
    
    // Kill existing timeline if any
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    setIsPlaying(true);
    onPlayStateChange?.(true);
    setProgress(0);
    setCurrentTime(0);

    const tl = gsap.timeline({
      onUpdate: function() {
        const p = this.progress() * 100;
        const t = this.time();
        setProgress(p);
        setCurrentTime(t);
      },
      onComplete: () => {
        setIsPlaying(false);
        onPlayStateChange?.(false);
        setProgress(0);
        setCurrentTime(0);
      }
    });
    timelineRef.current = tl;

    // Reset initial state based on entrance animation
    const setInitialState = () => {
      switch (introAnimation) {
        case "fade":
          gsap.set(introRef.current, { opacity: 0, scale: 1, x: 0, y: 0, clipPath: "inset(0 0 0 0)" });
          break;
        case "slide":
          gsap.set(introRef.current, { opacity: 0, scale: 1, x: -100, y: 0, clipPath: "inset(0 0 0 0)" });
          break;
        case "zoom":
          gsap.set(introRef.current, { opacity: 0, scale: 0.8, x: 0, y: 0, clipPath: "inset(0 0 0 0)" });
          break;
        case "reveal":
          gsap.set(introRef.current, { opacity: 1, scale: 1, x: 0, y: 0, clipPath: "inset(0 100% 0 0)" });
          break;
        case "revealUp":
          gsap.set(introRef.current, { opacity: 1, scale: 1, x: 0, y: 0, clipPath: "inset(100% 0 0 0)" });
          break;
        case "bounce":
          gsap.set(introRef.current, { opacity: 0, scale: 0.3, x: 0, y: 0, clipPath: "inset(0 0 0 0)" });
          break;
        default:
          gsap.set(introRef.current, { opacity: 1, scale: 1, x: 0, y: 0, clipPath: "inset(0 0 0 0)" });
      }
    };

    setInitialState();

    const baseOrigin = introAlignment.includes("left") ? "left center" : introAlignment.includes("right") ? "right center" : "center";
    gsap.set(introRef.current, { transformOrigin: baseOrigin });

    // Entrance
    switch (introAnimation) {
      case "fade":
        tl.to(introRef.current, { opacity: 1, duration: 0.8, ease: "power2.out" });
        break;
      case "slide":
        tl.to(introRef.current, { x: 0, opacity: 1, duration: 0.8, ease: "power2.out" });
        break;
      case "zoom":
        tl.to(introRef.current, { scale: 1, opacity: 1, duration: 0.8, ease: "back.out(1.7)" });
        break;
      case "reveal":
        tl.to(introRef.current, { clipPath: "inset(0 0% 0 0)", duration: 1.0, ease: "power2.inOut" });
        break;
      case "revealUp":
        tl.to(introRef.current, { clipPath: "inset(0% 0 0 0)", duration: 1.0, ease: "power2.inOut" });
        break;
      case "bounce":
        tl.to(introRef.current, { opacity: 1, scale: 1, duration: 0.8, ease: "back.out(2)" });
        break;
    }

    // Hold
    tl.to({}, { duration: recording ? 2.2 : 1.5 });

    // Exit
    switch (introAnimation) {
      case "fade":
        tl.to(introRef.current, { opacity: 0, duration: 0.8, ease: "power2.in" });
        break;
      case "slide":
        tl.to(introRef.current, { x: 100, opacity: 0, duration: 0.8, ease: "power2.in" });
        break;
      case "zoom":
        tl.to(introRef.current, { scale: 0.8, opacity: 0, duration: 0.8, ease: "power2.in" });
        break;
      case "reveal":
        tl.to(introRef.current, { clipPath: "inset(0 0 0 100%)", duration: 1.0, ease: "power2.inOut" });
        break;
      case "revealUp":
        tl.to(introRef.current, { clipPath: "inset(0 0 100% 0)", duration: 1.0, ease: "power2.inOut" });
        break;
      case "bounce":
        tl.to(introRef.current, { opacity: 0, scale: 0.5, duration: 0.8, ease: "back.in(2)" });
        break;
      default:
        tl.to(introRef.current, { opacity: 0, duration: 0.5 });
    }

    // Delay after exit before resetting (hold the blank screen)
    tl.to({}, { duration: 2.0 });

    if (!recording) {
      // Reset back to visible for editing
      tl.to(introRef.current, { opacity: 1, x: 0, scale: 1, clipPath: "none", duration: 0 });
    }

    // Set total time after all animations are added
    setTotalTime(tl.duration());
    */
    setIsPlaying(true);
    onPlayStateChange?.(true);
    setTimeout(() => {
      setIsPlaying(false);
      onPlayStateChange?.(false);
    }, 1000);
  });

  const stopPreview = contextSafe(() => {
    // NOTE: Preview animation commented out. Will be reimplemented later.
    /*
    if (timelineRef.current) {
      timelineRef.current.kill();
    }
    setIsPlaying(false);
    onPlayStateChange?.(false);
    setProgress(0);
    setCurrentTime(0);
    if (introRef.current) {
      gsap.set(introRef.current, { opacity: 1, x: 0, scale: 1, clipPath: "none" });
    }
    */
    setIsPlaying(false);
    onPlayStateChange?.(false);
  });

  // Handle external play trigger
  useEffect(() => {
    if (playTrigger) {
      playPreview();
    }
  }, [playTrigger]);

  // Handle external stop trigger
  useEffect(() => {
    if (stopTrigger) {
      stopPreview();
    }
  }, [stopTrigger]);

  // ─── Capture Intro Clip ───────────────────────────────────────────────────────
  // captureIntroClip: records 100 frames at 20 FPS and uploads them in batches of 20
  // to permanent storage, returning an array of public frame URLs.
  // recordIntroTest: dev-only wrapper that logs results.
  const { getToken } = useAuth();

  const captureIntroClip = async (): Promise<string[]> => {
    if (!captureIntroRef.current) throw new Error("Capture intro element not mounted");

    setIsRecording(true);
    setRecordingStatus("Setting up recorder...");

    const isLandscapeLocal = aspectRatio === "16:9";
    
    // We use a fixed reference width for the hidden container to ensure consistent layout.
    // These match typical preview dimensions where the styles were designed.
    const REF_W = isLandscapeLocal ? 800 : 394;
    const REF_H = isLandscapeLocal ? 450 : 700;
    
    const targetW = isLandscapeLocal ? 1920 : 1080;
    const targetH = isLandscapeLocal ? 1080 : 1920;
    
    const coordScale = targetW / REF_W;
    const capturePixelRatio = coordScale;
    
    const TOTAL_MS = 5000;
    const bgHex = introBgColor || "#000000";
    const alignment = introAlignment || "center";

    // ── 1. Pre-fetch Google Fonts as base64 (bypass CSSStyleSheet CORS) ────────
    setRecordingStatus("Fetching fonts...");
    let fontEmbedCSS = "";
    try {
      const GFONTS_URL =
        "https://fonts.googleapis.com/css2?" +
        "family=Playfair+Display:ital,wght@0,400..900;1,400..900" +
        "&family=Space+Mono:ital,wght@0,400;0,700;1,400;1,700" +
        "&family=Questrial&family=Oswald:wght@200..700" +
        "&family=Cinzel+Decorative:wght@400;700;900" +
        "&family=Cormorant+Garamond:ital,wght@0,300..700;1,300..700" +
        "&family=Montserrat:wght@100..900&family=Inter:wght@100..900" +
        "&family=Roboto:wght@100..900&family=Lora:ital,wght@0,400..700;1,400..700" +
        "&display=swap";
      const cssRes = await fetch(GFONTS_URL, {
        headers: { "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36" },
      });
      if (cssRes.ok) {
        let css = await cssRes.text();
        const fontUrls = [...css.matchAll(/url\(([^)]+)\)/g)]
          .map(m => m[1]?.replace(/['"]/g, ""))
          .filter((url): url is string => !!url);
        await Promise.all(fontUrls.map(async (url) => {
          try {
            const r = await fetch(url);
            if (!r.ok) return;
            const buf = await r.arrayBuffer();
            const bytes = new Uint8Array(buf);
            let bin = ""; bytes.forEach(b => bin += String.fromCharCode(b));
            const mime = url.includes(".woff2") ? "font/woff2" : "font/woff";
            css = css.replaceAll(url, `data:${mime};base64,${btoa(bin)}`);
          } catch { /* skip individual failures */ }
        }));
        fontEmbedCSS = css;
      }
    } catch (err) {
      console.warn("[captureIntroClip] font fetch failed:", err);
    }

    // ── 2. Capture ONE static frame ───────────────────────────────────────────
    setRecordingStatus("Rendering frame...");
    const { toCanvas } = await import("html-to-image");
    

    
    // TEMPORARILY force full visibility for the static frame capture
    // otherwise we might capture an invisible (opacity 0) box
    const originalStyle = captureIntroRef.current!.style.cssText;
    captureIntroRef.current!.style.opacity = "1";
    captureIntroRef.current!.style.transform = "none";
    captureIntroRef.current!.style.clipPath = "none";

    let staticFrame: HTMLCanvasElement;
    try {
      staticFrame = await toCanvas(captureIntroRef.current!, {
        pixelRatio: capturePixelRatio,
        fontEmbedCSS: fontEmbedCSS || undefined,
        skipFonts: !fontEmbedCSS,
      });
    } catch (err) {
      captureIntroRef.current!.style.cssText = originalStyle;
      setIsRecording(false); setRecordingStatus(null);
      onCapturePhaseChange?.(null, 0);
      throw new Error(`Static frame capture failed: ${err}`);
    }
    const boxW = staticFrame.width;
    const boxH = staticFrame.height;

    // ── 3. Calculate base position from DOM (at t=0) ─────────────────────────
    // Important: we do this WHILE the transform is "none" to get the natural box position
    const rect = captureIntroRef.current!.getBoundingClientRect();
    const pRect = captureContainerRef.current!.getBoundingClientRect();
    
    captureIntroRef.current!.style.cssText = originalStyle;
    
    // Important: subtract any initial transform from the rect to get the "true" base position
    const startSt = window.getComputedStyle(captureIntroRef.current!);
    const startMatrix = new DOMMatrix(startSt.transform);
    
    let baseX = (rect.left - pRect.left - startMatrix.e) * coordScale;
    let baseY = (rect.top - pRect.top - startMatrix.f) * coordScale;

    // ── 4. Set up Canvas & Draw ────────────────────────────────────────────
    const canvas = document.createElement("canvas");
    canvas.width = targetW; canvas.height = targetH;
    const ctx = canvas.getContext("2d", { alpha: true })!;

    ctx.clearRect(0, 0, targetW, targetH);
    ctx.save();
    ctx.globalAlpha = 1.0;
    const cx = baseX + boxW / 2;
    const cy = baseY + boxH / 2;
    ctx.translate(cx, cy);
    ctx.drawImage(staticFrame, -boxW / 2, -boxH / 2);
    ctx.restore();

    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((b) => resolve(b!), "image/png");
    });

    // ── 5. Upload single frame ───────────────────────────────────────
    onCapturePhaseChange?.("processing", 50);
    setRecordingStatus("Uploading opening title...");
    const token = await getToken();

    const file = new File([blob], `frame_000.png`, { type: "image/png" });
    const resp = await fetch(`${config.apiUrl}/media/upload`, {
      method: "POST",
      body: file,
      headers: {
        "Content-Type": "image/png",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!resp.ok) throw new Error(`Frame upload failed: ${resp.statusText}`);
    const { url } = await resp.json();

    setIsRecording(false);
    setRecordingStatus(null);
    onCapturePhaseChange?.(null, 0);
    stopPreview();

    return [url as string];
  };

  // Expose captureIntroClip to parent — supports both RefObject and callback ref
  useEffect(() => {
    if (!captureIntroClipRef) return;
    const handle = { captureIntroClip };
    if (typeof captureIntroClipRef === "function") {
      captureIntroClipRef(handle);
      return () => captureIntroClipRef(null);
    } else {
      (captureIntroClipRef as React.MutableRefObject<typeof handle | null>).current = handle;
      return () => { (captureIntroClipRef as React.MutableRefObject<typeof handle | null>).current = null; };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureIntroClipRef]);

  // Dev-only: captures and logs results
  const recordIntroTest = async () => {
    if (isRecording || isPlaying) return;
    try {
      const urls = await captureIntroClip();
      stopPreview();
      console.log(`[recordIntroTest] Captured ${urls.length} frames:`, urls.slice(0, 3));
    } catch (err) {
      console.error("[recordIntroTest] failed:", err);
      setIsRecording(false); setRecordingStatus(null);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────


  // ─────────────────────────────────────────────────────────────────────────────


  const contrastTextColor = introTextColor || getContrastColor(introBgColor);
  const isLandscape = aspectRatio === "16:9";
  const currentPlaceholderImage = isLandscape
    ? placeholderImage
    : portraitPlaceholderImage;

  // BASE COORDINATE SYSTEM (Matches backend/video/stitch.ts)
  // We use 1280 as the reference width for logo size
  // We use 720 as the reference for padding (shorter side)
  const virtualWidth = isLandscape ? 1280 : 720;
  const sizeBase = 1280;
  const paddingBase = 720;

  const adjustedSize = (watermarkSize * sizeBase) / virtualWidth;
  const adjustedPadding = (watermarkPadding * paddingBase) / virtualWidth;

  // Font mapping for intro styles
  const getIntroFontClass = () => {
    if (introFont) return `font-${introFont}`;
    
    switch (introStyle) {
      case "cinematic": return "font-playfair";
      case "architect": return "font-space";
      case "editorial": return "font-questrial";
      case "geometric": return "font-oswald";
      case "typewriter": return "font-space";
      default: return "font-sans";
    }
  };

  return (
    <div className="flex flex-col">
      <div className={cn(
        "relative mt-2 2k:mt-5 4k:mt-8 w-full flex items-center justify-center bg-black/5 rounded-2xl transition-all duration-500",
        !isPlaying ? "overflow-visible" : "overflow-hidden",
        isLandscape ? "aspect-video" : "aspect-9/16 max-h-[700px] mx-auto"
      )}>
        <div
          ref={previewContainerRef}
          className={cn(
            "relative h-full shadow-xl transition-all duration-300",
            !isPlaying ? "overflow-visible" : "overflow-hidden",
            isLandscape ? "w-full" : "aspect-9/16",
          )}
        >
          <img
            src={currentPlaceholderImage}
            alt="Preview"
            className="absolute inset-0 w-full h-full object-cover rounded-2xl"
          />

          {logoEnabled && (
            <div
              className={cn(
                "absolute inset-0 flex transition-opacity duration-300",
                activeTab !== "logo" && "opacity-20 grayscale",
              )}
              style={{
                padding: `${adjustedPadding}%`,
                ...getContainerAlignment(watermarkAlignment),
              }}
            >
              <img
                src={watermarkPreview}
                alt="Watermark"
                className="relative"
                style={{
                  width: `${adjustedSize}%`,
                  opacity: watermarkOpacity / 100,
                }}
              />
            </div>
          )}

          {activeTab === "intro" && (
            <div 
              className={cn(
                "absolute inset-0 flex p-2 pointer-events-none transition-opacity duration-300",
                !introEnabled && "opacity-20 grayscale"
              )}
              style={getContainerAlignment(introAlignment)}
            >
              {!introEnabled && (
                <div className="absolute top-4 left-4 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/20 text-[10px] font-bold text-white uppercase tracking-widest z-50">
                  {t("createVideo.disabled") || "Disabled"}
                </div>
              )}
              <div
                className={cn("relative group", isPlaying && "cursor-not-allowed pointer-events-auto")}
              >
                <div
                  key={introStyle}
                  className="relative pointer-events-auto"
                  style={{
                    padding: isLandscape ? "1rem" : "0.5rem",
                  }}
                >
                  <div
                  ref={introRef}
                  className={cn(
                    "text-center relative",
                    getIntroFontClass(),
                    introStyle === "cinematic" && cn("tracking-[0.3em] py-4 border-y border-white/10 shadow-none px-6"),
                    introStyle === "architect" && cn("py-6 border-none shadow-none px-6"),
                    introStyle === "editorial" && cn(
                      "py-6 border border-white/40 outline outline-1 outline-white/20 outline-offset-4 shadow-none bg-transparent",
                      "tracking-widest px-6"
                    ),
                    introStyle === "geometric" && cn(
                      "font-black py-6 border-[4px] border-white",
                      "tracking-tighter ring-1 ring-white/20 relative px-6"
                    ),
                    introStyle === "typewriter" && cn("bg-black/90 py-3 border-l-4 border-white/60 text-left shadow-none px-6"),
                  )}
                  style={{
                    padding: isLandscape ? "1.25rem" : "1rem",
                    paddingLeft: !isLandscape ? "1.5rem" : undefined,
                    paddingRight: !isLandscape ? "1.5rem" : undefined,
                    backgroundColor: `${introBgColor}${Math.round((introBgOpacity / 100) * 255)
                      .toString(16)
                      .padStart(2, "0")}`,
                    color: contrastTextColor,
                    outlineColor: contrastTextColor + "4d",
                    // @ts-ignore
                    "--bracket-color": contrastTextColor,
                    // @ts-ignore
                    "--shadow-opacity": (introBgOpacity / 200).toFixed(2),
                    backgroundImage: introStyle === "architect" ? `
                      linear-gradient(to right, var(--bracket-color) 12px, transparent 12px),
                      linear-gradient(to bottom, var(--bracket-color) 12px, transparent 12px),
                      linear-gradient(to left, var(--bracket-color) 12px, transparent 12px),
                      linear-gradient(to bottom, var(--bracket-color) 12px, transparent 12px),
                      linear-gradient(to right, var(--bracket-color) 12px, transparent 12px),
                      linear-gradient(to top, var(--bracket-color) 12px, transparent 12px),
                      linear-gradient(to left, var(--bracket-color) 12px, transparent 12px),
                      linear-gradient(to top, var(--bracket-color) 12px, transparent 12px)
                    ` : undefined,
                    backgroundPosition: introStyle === "architect" ? "8px 8px, 8px 8px, calc(100% - 8px) 8px, calc(100% - 8px) 8px, 8px calc(100% - 8px), 8px calc(100% - 8px), calc(100% - 8px) calc(100% - 8px), calc(100% - 8px) calc(100% - 8px)" : undefined,
                    backgroundSize: introStyle === "architect" ? "16px 2px, 2px 16px, 16px 2px, 2px 16px, 16px 2px, 2px 16px, 16px 2px, 2px 16px" : undefined,
                    backgroundRepeat: "no-repeat",
                  }}
                >
                <RichTextEditor
                  minimal
                  disabled={isPlaying}
                  content={introTitle || ""}
                  onChange={onIntroTitleChange || (() => {})}
                  className={cn(
                    introStyle !== "editorial" && introStyle !== "typewriter" && "drop-shadow-2xl text-shadow-lg",
                    "prose-sm prose-tight max-w-none",
                    introAlignment.includes("left") ? "text-left" : introAlignment.includes("right") ? "text-right" : "text-center",
                    introStyle === "cinematic" ? "font-medium" : "font-bold",
                    contrastTextColor === "#FFFFFF" && "prose-invert"
                  )}
                  style={{
                    fontSize: isLandscape ? "0.875rem" : "0.75rem",
                  }}
                  placeholder={t("createVideo.brandingIntroPlaceholder")}
                />
                
                </div>
              </div>
            </div>
          </div>
          )}

          {activeTab === "outro" && (
            <div className="absolute inset-0 flex items-center justify-center p-8 bg-black/60 animate-in fade-in duration-500">
              <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 text-center space-y-4 max-w-[80%]">
                <div className="w-12 h-1 bg-white/40 mx-auto rounded-full" />
                <RichTextEditor
                  minimal
                  disabled={isPlaying}
                  content={contactCard || ""}
                  onChange={onContactCardChange || (() => {})}
                  className="text-white/80 text-xs sm:text-sm 2k:text-xl 4k:text-3xl font-medium whitespace-pre-wrap leading-relaxed prose-sm prose-tight prose-invert max-w-none text-center"
                  placeholder={t("createVideo.brandingOutroPlaceholder")}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar (Seekbar style) - MOVED OUTSIDE CARD */}
      <div className={cn(
        "w-full transition-all duration-300 overflow-hidden",
        isPlaying ? "opacity-100 mt-6 h-auto" : "opacity-0 h-0"
      )}>
        <div className="flex justify-between items-center mb-2 px-1">
          <span className="text-[10px] 2k:text-lg 4k:text-2xl font-bold uppercase tracking-widest text-black/40 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-black"></span>
            </span>
            {t("createVideo.previewingAnimation") || "Previewing Animation"}
          </span>
          <span className="text-[10px] 2k:text-lg 4k:text-2xl font-mono text-black/40">
            {Math.round(currentTime)}s / {Math.round(totalTime)}s
          </span>
        </div>
        <div className="h-1.5 w-full bg-black/5 rounded-full overflow-hidden">
          <div 
            className="h-full bg-black"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* ── Record Test Button (dev validation only — hidden, kept for future use) ── */}
      {false && activeTab === "intro" && (
        <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-black/5">
          <span className="text-[10px] font-mono text-black/30 uppercase tracking-widest">Dev Test</span>
          <button
            type="button"
            disabled={isPlaying || isRecording}
            onClick={recordIntroTest}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all border",
              isRecording
                ? "bg-red-500 text-white border-red-500 cursor-not-allowed"
                : "bg-black/5 text-black/60 border-black/10 hover:bg-red-500 hover:text-white hover:border-red-500",
              (isPlaying || isRecording) && "opacity-50 cursor-not-allowed"
            )}
            title="Record a test .webm of the intro animation to validate MediaRecorder approach"
          >
            <CircleDot className={cn("w-3 h-3", isRecording && "animate-pulse")} />
            {isRecording ? (recordingStatus || "Recording...") : "Record Test"}
          </button>
        </div>
      )}

      {/* Hidden Capture Element (always rendered, off-screen at reference resolution) */}
      <div 
        ref={captureContainerRef}
        className="fixed left-[-9999px] top-0 pointer-events-none overflow-hidden bg-black"
        style={{
          width: isLandscape ? 800 : 394,
          height: isLandscape ? 450 : 700,
        }}
      >
        <div 
          className="absolute inset-0 flex p-2"
          style={getContainerAlignment(introAlignment)}
        >
          <div className="relative pointer-events-auto">
            <div
              className="relative"
              style={{
                padding: isLandscape ? "1rem" : "0.5rem",
              }}
            >
              <div
                ref={captureIntroRef}
              className={cn(
                "text-center relative",
                getIntroFontClass(),
                introStyle === "cinematic" && cn("tracking-[0.3em] py-4 border-y border-white/10 shadow-none px-6"),
                introStyle === "architect" && cn("py-6 border-none shadow-none px-6"),
                introStyle === "editorial" && cn(
                  "py-6 border border-white/40 outline outline-1 outline-white/20 outline-offset-4 shadow-none bg-transparent",
                  "tracking-widest px-6"
                ),
                introStyle === "geometric" && cn(
                  "font-black py-6 border-[4px] border-white",
                  "tracking-tighter ring-1 ring-white/20 relative px-6"
                ),
                introStyle === "typewriter" && cn("bg-black/90 py-3 border-l-4 border-white/60 text-left shadow-none px-6"),
              )}
              style={{
                padding: isLandscape ? "1.25rem" : "1rem",
                paddingLeft: !isLandscape ? "1.5rem" : undefined,
                paddingRight: !isLandscape ? "1.5rem" : undefined,
                backgroundColor: `${introBgColor}${Math.round((introBgOpacity / 100) * 255)
                  .toString(16)
                  .padStart(2, "0")}`,
                color: contrastTextColor,
                outlineColor: contrastTextColor + "4d",
                // @ts-ignore
                "--bracket-color": contrastTextColor,
                // @ts-ignore
                "--shadow-opacity": (introBgOpacity / 200).toFixed(2),
                backgroundImage: introStyle === "architect" ? `
                  linear-gradient(to right, var(--bracket-color) 12px, transparent 12px),
                  linear-gradient(to bottom, var(--bracket-color) 12px, transparent 12px),
                  linear-gradient(to left, var(--bracket-color) 12px, transparent 12px),
                  linear-gradient(to bottom, var(--bracket-color) 12px, transparent 12px),
                  linear-gradient(to right, var(--bracket-color) 12px, transparent 12px),
                  linear-gradient(to top, var(--bracket-color) 12px, transparent 12px),
                  linear-gradient(to left, var(--bracket-color) 12px, transparent 12px),
                  linear-gradient(to top, var(--bracket-color) 12px, transparent 12px)
                ` : undefined,
                backgroundPosition: introStyle === "architect" ? "8px 8px, 8px 8px, calc(100% - 8px) 8px, calc(100% - 8px) 8px, 8px calc(100% - 8px), 8px calc(100% - 8px), calc(100% - 8px) calc(100% - 8px), calc(100% - 8px) calc(100% - 8px)" : undefined,
                backgroundSize: introStyle === "architect" ? "16px 2px, 2px 16px, 16px 2px, 2px 16px, 16px 2px, 2px 16px, 16px 2px, 2px 16px" : undefined,
                backgroundRepeat: "no-repeat",
              }}
            >
              <div 
                className={cn(
                  "prose prose-sm max-w-none text-center prose-tight",
                  introStyle !== "editorial" && introStyle !== "typewriter" && "drop-shadow-2xl text-shadow-lg",
                  introAlignment.includes("left") ? "text-left" : introAlignment.includes("right") ? "text-right" : "text-center",
                  introStyle === "cinematic" ? "font-medium" : "font-bold",
                  contrastTextColor === "#FFFFFF" && "prose-invert"
                )}
                style={{
                  fontSize: isLandscape ? "0.875rem" : "0.75rem",
                }}
                dangerouslySetInnerHTML={{ __html: introTitle || "" }}
              />
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
